import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const CreateMeetingModal = ({ isOpen, onClose, onMeetingCreated }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    meetingDate: '',
    meetingTime: '',
    durationMinutes: 60,
    locationType: 'online',
    locationDetails: '',
    googleMeetLink: '',
    teamId: '',
    attendeeIds: []
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchTeamsAndUsers();
    }
  }, [isOpen]);

  const fetchTeamsAndUsers = async () => {
    try {
      const { data: teamsData } = await supabase
        ?.from('teams')
        ?.select('id, team_name')
        ?.order('team_name');

      const { data: usersData } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, role')
        ?.order('full_name');

      setTeams(teamsData || []);
      setUsers(usersData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.title?.trim()) newErrors.title = 'Title is required';
    if (!formData?.meetingDate) newErrors.meetingDate = 'Date is required';
    if (!formData?.meetingTime) newErrors.meetingTime = 'Time is required';
    if (formData?.attendeeIds?.length === 0) newErrors.attendeeIds = 'Select at least one attendee';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const { data: meeting, error: meetingError } = await supabase
        ?.from('meetings')
        ?.insert({
          title: formData?.title,
          description: formData?.description,
          meeting_date: formData?.meetingDate,
          meeting_time: formData?.meetingTime,
          duration_minutes: formData?.durationMinutes,
          location_type: formData?.locationType,
          location_details: formData?.locationDetails,
          google_meet_link: formData?.googleMeetLink,
          team_id: formData?.teamId || null,
          created_by: user?.id,
          status: 'scheduled'
        })
        ?.select()
        ?.single();

      if (meetingError) throw meetingError;

      // Add attendees
      if (formData?.attendeeIds?.length > 0) {
        const attendeeRecords = formData?.attendeeIds?.map(userId => ({
          meeting_id: meeting?.id,
          user_id: userId
        }));

        const { error: attendeeError } = await supabase
          ?.from('meeting_attendees')
          ?.insert(attendeeRecords);

        if (attendeeError) throw attendeeError;
      }

      // Create notifications for attendees
      const notifications = formData?.attendeeIds?.map(userId => ({
        user_id: userId,
        type: 'meeting_scheduled',
        title: 'New Meeting Scheduled',
        message: `You have been invited to "${formData?.title}" on ${formData?.meetingDate}`,
        reference_type: 'meeting',
        reference_id: meeting?.id
      }));

      await supabase?.from('notifications')?.insert(notifications);

      onMeetingCreated();
    } catch (error) {
      console.error('Error creating meeting:', error);
      setErrors({ submit: error?.message });
    } finally {
      setLoading(false);
    }
  };

  const toggleAttendee = (userId) => {
    setFormData(prev => ({
      ...prev,
      attendeeIds: prev?.attendeeIds?.includes(userId)
        ? prev?.attendeeIds?.filter(id => id !== userId)
        : [...prev?.attendeeIds, userId]
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Create Meeting</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Meeting Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData?.title}
              onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
              className={`w-full px-4 py-2 border ${errors?.title ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter meeting title"
            />
            {errors?.title && <p className="mt-1 text-sm text-red-500">{errors?.title}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={formData?.description}
              onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[100px]"
              placeholder="Enter meeting description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData?.meetingDate}
                onChange={(e) => setFormData({ ...formData, meetingDate: e?.target?.value })}
                className={`w-full px-4 py-2 border ${errors?.meetingDate ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500`}
              />
              {errors?.meetingDate && <p className="mt-1 text-sm text-red-500">{errors?.meetingDate}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={formData?.meetingTime}
                onChange={(e) => setFormData({ ...formData, meetingTime: e?.target?.value })}
                className={`w-full px-4 py-2 border ${errors?.meetingTime ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500`}
              />
              {errors?.meetingTime && <p className="mt-1 text-sm text-red-500">{errors?.meetingTime}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Duration (minutes)</label>
              <input
                type="number"
                value={formData?.durationMinutes}
                onChange={(e) => setFormData({ ...formData, durationMinutes: parseInt(e?.target?.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                min="15"
                step="15"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location Type</label>
              <select
                value={formData?.locationType}
                onChange={(e) => setFormData({ ...formData, locationType: e?.target?.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="online">Online</option>
                <option value="in-person">In-Person</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
          </div>

          {formData?.locationType === 'in-person' || formData?.locationType === 'hybrid' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location Details</label>
              <input
                type="text"
                value={formData?.locationDetails}
                onChange={(e) => setFormData({ ...formData, locationDetails: e?.target?.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter location address"
              />
            </div>
          ) : null}

          {formData?.locationType === 'online' || formData?.locationType === 'hybrid' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Google Meet Link</label>
              <input
                type="url"
                value={formData?.googleMeetLink}
                onChange={(e) => setFormData({ ...formData, googleMeetLink: e?.target?.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="https://meet.google.com/..."
              />
            </div>
          ) : null}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Team (Optional)</label>
            <select
              value={formData?.teamId}
              onChange={(e) => setFormData({ ...formData, teamId: e?.target?.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">No specific team</option>
              {teams?.map((team) => (
                <option key={team?.id} value={team?.id}>
                  {team?.team_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Attendees <span className="text-red-500">*</span>
            </label>
            <div className="border border-gray-300 rounded-lg p-4 max-h-60 overflow-y-auto">
              {users?.map((user) => (
                <label key={user?.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData?.attendeeIds?.includes(user?.id)}
                    onChange={() => toggleAttendee(user?.id)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                    <p className="text-xs text-gray-500 capitalize">{user?.role?.replace('_', ' ')}</p>
                  </div>
                </label>
              ))}
            </div>
            {errors?.attendeeIds && <p className="mt-1 text-sm text-red-500">{errors?.attendeeIds}</p>}
          </div>

          {errors?.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errors?.submit}</p>
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={loading}
              iconName={loading ? 'Loader2' : 'Plus'}
              iconPosition="left"
              className="flex-1"
            >
              {loading ? 'Creating...' : 'Create Meeting'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateMeetingModal;